import { useEffect, useRef, useState } from 'react';
import useActiveResident from './useActiveResident';
import { fetchFacilityDetails } from '../services/Facilities';
import { fetchResidentPaymentTransactions } from '../services/Payments';

type NeighborsPaymentAccessCore = {
    facilityPaymentsEnabled: boolean;
    hasPaymentHistory: boolean;
    showPaymentsEntry: boolean;
    showOrderPaymentUI: boolean;
    canManageCards: boolean;
    // The facility settings call failed, so whether card payments are on is unknown.
    // Order screens treat this as "payments on" and show the could-not-verify gate.
    facilityLookupFailed: boolean;
    loading: boolean;
};

export type NeighborsPaymentAccess = NeighborsPaymentAccessCore & {
    resolvedShowPaymentsEntry: boolean;
    resolvedShowOrderPaymentUI: boolean;
    resolvedFacilityPaymentsEnabled: boolean;
    resolvedCanManageCards: boolean;
    retry: () => void;
};

export const getFacilityPaymentsEnabled = (facilityDetails: any): boolean => {
    const configurations = facilityDetails?.featureConfigs?.configurations || {};
    return Boolean(configurations.creditCardPayments || configurations.CreditCardPaymentsEnabled);
};

const hasResidentPaymentHistory = async (residentId: string, facilityId: string): Promise<boolean> => {
    const response = await fetchResidentPaymentTransactions({
        residentId,
        facilityId,
        page: 1,
        pageSize: 1,
    });

    const totalRecords = Number(
        response?.TotRecords
        ?? response?.pagination?.totalCount
        ?? 0,
    );

    return totalRecords > 0 || Boolean(response?.items?.length);
};

const defaultState: NeighborsPaymentAccessCore = {
    facilityPaymentsEnabled: false,
    hasPaymentHistory: false,
    showPaymentsEntry: false,
    showOrderPaymentUI: false,
    canManageCards: false,
    facilityLookupFailed: false,
    loading: true,
};

const PAYMENT_ACCESS_CACHE_TTL_MS = 60 * 1000;
type CachedAccessState = {
    value: Omit<NeighborsPaymentAccessCore, 'loading'>;
    expiresAt: number;
};

const paymentAccessCache = new Map<string, CachedAccessState>();
const paymentAccessInFlight = new Map<string, Promise<CachedAccessState['value']>>();
// Mounted hooks re-fetch when any of them retries, so every screen sees the new result.
const retryListeners = new Set<() => void>();

const createResolvedState = (
    baseState: NeighborsPaymentAccessCore,
    retry: () => void,
): NeighborsPaymentAccess => ({
    ...baseState,
    retry,
    resolvedShowPaymentsEntry: baseState.loading ? true : baseState.showPaymentsEntry,
    resolvedShowOrderPaymentUI: baseState.loading ? true : baseState.showOrderPaymentUI,
    resolvedFacilityPaymentsEnabled: baseState.loading || baseState.facilityLookupFailed ? true : baseState.facilityPaymentsEnabled,
    resolvedCanManageCards: baseState.loading ? true : baseState.canManageCards,
});

const fetchAccessState = async (residentId: string, facilityId: string): Promise<CachedAccessState['value']> => {
    // The two lookups do not depend on each other, so they run together: the wait is the slower of the
    // two, not their sum. Each one catches its own failure, exactly as when they ran one after the other.
    const lookupFacility = async () => {
        try {
            const facilityDetails = await fetchFacilityDetails(facilityId);
            return { facilityPaymentsEnabled: getFacilityPaymentsEnabled(facilityDetails), facilityLookupFailed: false };
        } catch (_error) {
            return { facilityPaymentsEnabled: false, facilityLookupFailed: true };
        }
    };
    const lookupHistory = async () => {
        try {
            return { hasPaymentHistory: await hasResidentPaymentHistory(residentId, facilityId), historyLookupFailed: false };
        } catch (_error) {
            return { hasPaymentHistory: false, historyLookupFailed: true };
        }
    };
    const [{ facilityPaymentsEnabled, facilityLookupFailed }, { hasPaymentHistory, historyLookupFailed }] = await Promise.all([
        lookupFacility(),
        lookupHistory(),
    ]);

    // Fail-open: keep Payments entry visible when history probe fails while facility payments are OFF.
    const showPaymentsEntry = facilityPaymentsEnabled || hasPaymentHistory || historyLookupFailed || facilityLookupFailed;

    return {
        facilityPaymentsEnabled,
        hasPaymentHistory,
        showPaymentsEntry,
        // Phase decision: order payment UI follows the same visibility rule as Payments entry.
        showOrderPaymentUI: showPaymentsEntry,
        canManageCards: facilityPaymentsEnabled,
        facilityLookupFailed,
    };
};

const retryPaymentAccess = () => {
    paymentAccessCache.clear();
    // Drop pending lookups too, so the retry asks again instead of reusing a request that is failing.
    paymentAccessInFlight.clear();
    retryListeners.forEach((listener) => listener());
};

const useNeighborsPaymentAccess = (): NeighborsPaymentAccess => {
    const activeResident = useActiveResident();
    const residentId = activeResident?._id ? String(activeResident._id) : '';
    const facilityId = activeResident?.Facility ? String(activeResident.Facility) : '';

    const [state, setState] = useState<NeighborsPaymentAccessCore>(defaultState);
    const lastResolvedStateRef = useRef<CachedAccessState['value'] | null>(null);
    const [reloadCount, setReloadCount] = useState(0);

    useEffect(() => {
        const listener = () => setReloadCount((count) => count + 1);
        retryListeners.add(listener);
        return () => {
            retryListeners.delete(listener);
        };
    }, []);

    useEffect(() => {
        if (!residentId || !facilityId) {
            lastResolvedStateRef.current = null;
            setState({
                ...defaultState,
                loading: false,
            });
            return;
        }

        let isMounted = true;

        (async () => {
            const cacheKey = `${residentId}:${facilityId}`;
            const currentTime = Date.now();
            const cachedEntry = paymentAccessCache.get(cacheKey);

            if (cachedEntry && cachedEntry.expiresAt > currentTime) {
                lastResolvedStateRef.current = cachedEntry.value;
                if (isMounted) {
                    setState({
                        ...cachedEntry.value,
                        loading: false,
                    });
                }
                return;
            }

            if (isMounted) {
                setState((prevState) => ({
                    ...prevState,
                    loading: true,
                }));
            }

            let inFlightRequest = paymentAccessInFlight.get(cacheKey);
            if (!inFlightRequest) {
                inFlightRequest = fetchAccessState(residentId, facilityId);
                paymentAccessInFlight.set(cacheKey, inFlightRequest);
            }

            try {
                const fetchedState = await inFlightRequest;
                // A failed facility lookup is not remembered, so the next screen asks again.
                if (!fetchedState.facilityLookupFailed) {
                    paymentAccessCache.set(cacheKey, {
                        value: fetchedState,
                        expiresAt: Date.now() + PAYMENT_ACCESS_CACHE_TTL_MS,
                    });
                }
                lastResolvedStateRef.current = fetchedState;

                if (isMounted) {
                    setState({
                        ...fetchedState,
                        loading: false,
                    });
                }
            } catch (_error) {
                if (isMounted) {
                    setState(() => {
                        const lastResolvedState = lastResolvedStateRef.current;
                        if (lastResolvedState) {
                            return {
                                ...lastResolvedState,
                                loading: false,
                            };
                        }

                        return {
                            facilityPaymentsEnabled: false,
                            hasPaymentHistory: false,
                            showPaymentsEntry: true,
                            // Phase decision: keep order payment UI aligned with Payments entry visibility.
                            showOrderPaymentUI: true,
                            canManageCards: false,
                            facilityLookupFailed: true,
                            loading: false,
                        };
                    });
                }
            } finally {
                const currentRequest = paymentAccessInFlight.get(cacheKey);
                if (currentRequest === inFlightRequest) {
                    paymentAccessInFlight.delete(cacheKey);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [residentId, facilityId, reloadCount]);

    return createResolvedState(state, retryPaymentAccess);
};

export default useNeighborsPaymentAccess;
